/**
 * UI helpers for SMS length vs paste / multi-line input.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  SMS_TEXT_MAX_LENGTH,
  smsEffectiveGsmLength,
} from '@/features/campaign-texts/smsTextLimit';

import { parseSmsTextsInput, splitLines } from './PrepareNewDropsForm.utils';

export interface SmsTextsOverLengthState {
  overLimit: boolean;
  overCount: number;
  maxLen: number;
}

function measure(segments: string[], limit: number): SmsTextsOverLengthState {
  const lengths = segments.map((s) => smsEffectiveGsmLength(s));
  const overCount = lengths.filter((n) => n > limit).length;
  return {
    overLimit: overCount > 0,
    overCount,
    maxLen: lengths.length ? Math.max(...lengths) : 0,
  };
}

/**
 * For paste textarea: detect any SMS segment over GSM single-segment limit.
 * A pasted JSON array is measured per text — never as one blob; only an
 * unparseable JSON blob is measured whole.
 */
export function smsTextsOverLengthUiState(
  value: string,
  limit: number = SMS_TEXT_MAX_LENGTH,
): SmsTextsOverLengthState {
  const raw = String(value || '').trim();
  if (!raw) return measure([], limit);

  const { texts, error } = parseSmsTextsInput(value);
  if (!error && texts.length > 0) return measure(texts, limit);

  const looksLikeJson = raw.startsWith('[') || raw.startsWith('{');
  if (looksLikeJson) return measure(error ? [raw] : [], limit);
  return measure(splitLines(value), limit);
}

/** Wording of the over-length warning; null when every text fits one segment. */
export function smsTextsOverLengthMessage(
  state: SmsTextsOverLengthState,
  limit: number = SMS_TEXT_MAX_LENGTH,
): string | null {
  if (!state.overLimit) return null;
  const plural = state.overCount !== 1 ? 's' : '';
  return `${state.overCount} SMS text${plural} over ${limit} effective GSM characters (longest ${state.maxLen}). Shorten before sending.`;
}

/**
 * Single hint line for a paste-capable text input: the parse error when the
 * input is broken JSON, otherwise the per-text over-length warning.
 */
export function smsTextsInputHint(
  value: string,
  limit: number = SMS_TEXT_MAX_LENGTH,
): string | null {
  const { error } = parseSmsTextsInput(value);
  if (error) return error;
  return smsTextsOverLengthMessage(smsTextsOverLengthUiState(value, limit), limit);
}
